import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Heart,
  ShieldCheck,
  Package,
  Gift,
  GraduationCap,
  Sparkles,
  ExternalLink,
  Plus,
  Pencil,
  Trash2,
  Search,
  CheckCircle2,
  HelpCircle,
  Briefcase,
  Activity,
  Award,
  Zap,
} from "lucide-react";

const ICON_MAP = {
  Heart: Heart,
  ShieldCheck: ShieldCheck,
  Package: Package,
  Gift: Gift,
  GraduationCap: GraduationCap,
  Sparkles: Sparkles,
  Briefcase: Briefcase,
  Activity: Activity,
  Award: Award,
  Zap: Zap,
};

const CATEGORIAS = [
  "Todas",
  "Seguros y Salud",
  "Convenios y Envíos",
  "Bienestar",
  "Desarrollo",
  "Financiero",
];

export default function BeneficiosPage() {
  const { user } = useAuth();
  const isAdminOrRRHH = user?.rol === "administrador" || user?.rol === "rrhh";

  const [beneficios, setBeneficios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todas");

  // Modal Detalle
  const [detailModal, setDetailModal] = useState(null);

  // Modal Crear / Editar
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    titulo: "",
    categoria: "Seguros y Salud",
    descripcion_corta: "",
    descripcion_larga: "",
    icono: "Heart",
    enlace_contacto: "",
    requisitos: "",
    activo: true,
    destacado: false,
    orden: 0,
  });
  const [saving, setSaving] = useState(false);

  const loadBeneficios = async () => {
    try {
      setLoading(true);
      const res = await api.getBeneficios();
      setBeneficios(res.data || []);
    } catch (err) {
      console.error("Error al cargar beneficios:", err);
      toast.error("Error al cargar el catálogo de beneficios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBeneficios();
  }, []);

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
      titulo: "",
      categoria: "Seguros y Salud",
      descripcion_corta: "",
      descripcion_larga: "",
      icono: "Heart",
      enlace_contacto: "",
      requisitos: "",
      activo: true,
      destacado: false,
      orden: 0,
    });
    setIsEditOpen(true);
  };

  const handleOpenEdit = (item, e) => {
    e.stopPropagation();
    setEditingItem(item);
    setFormData({
      titulo: item.titulo,
      categoria: item.categoria || "Seguros y Salud",
      descripcion_corta: item.descripcion_corta || "",
      descripcion_larga: item.descripcion_larga || "",
      icono: item.icono || "Heart",
      enlace_contacto: item.enlace_contacto || "",
      requisitos: item.requisitos || "",
      activo: item.activo,
      destacado: item.destacado,
      orden: item.orden || 0,
    });
    setIsEditOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.titulo.trim() || !formData.descripcion_corta.trim()) {
      toast.error("El título y la descripción corta son obligatorios");
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await api.updateBeneficio(editingItem.id, formData);
        toast.success("Beneficio actualizado correctamente");
      } else {
        await api.createBeneficio(formData);
        toast.success("Nuevo beneficio agregado a Corazón Naranja");
      }
      setIsEditOpen(false);
      loadBeneficios();
    } catch (err) {
      console.error("Error guardando beneficio:", err);
      toast.error(err.response?.data?.detail || "Error al guardar el beneficio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Estás seguro de eliminar el beneficio "${item.titulo}"?`)) {
      return;
    }
    try {
      await api.deleteBeneficio(item.id);
      toast.success("Beneficio eliminado");
      loadBeneficios();
    } catch (err) {
      toast.error("Error al eliminar beneficio");
    }
  };

  const filteredBeneficios = beneficios.filter((b) => {
    const matchesCat =
      selectedCategory === "Todas" || b.categoria?.toLowerCase() === selectedCategory.toLowerCase();
    const matchesSearch =
      b.titulo?.toLowerCase().includes(search.toLowerCase()) ||
      b.descripcion_corta?.toLowerCase().includes(search.toLowerCase()) ||
      b.categoria?.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12 animate-fadeIn">
      {/* Banner Hero Corazón Naranja */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-orange-600 via-amber-500 to-orange-500 p-8 sm:p-10 text-white shadow-xl">
        <div className="absolute right-0 top-0 translate-x-10 -translate-y-10 opacity-15 pointer-events-none">
          <Heart className="w-96 h-96 fill-white" />
        </div>
        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-xs font-semibold mb-4 shadow-sm border border-white/25">
            <Heart className="w-3.5 h-3.5 fill-rose-300 text-rose-300" />
            Beneficios Exclusivos CPS
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight">
            Corazón Naranja 🧡
          </h1>
          <p className="mt-2.5 text-orange-100 text-sm sm:text-base leading-relaxed">
            Descubre los programas de bienestar, seguros médicos, planes preferenciales con CPX y convenios especiales diseñados para impulsar tu tranquilidad y la de tu familia.
          </p>

          <div className="flex items-center gap-3 mt-6">
            <Badge className="bg-white/90 text-orange-800 hover:bg-white text-xs px-3 py-1 font-semibold">
              {beneficios.length} Beneficios Vigentes
            </Badge>
            {isAdminOrRRHH && (
              <Button
                onClick={handleOpenCreate}
                className="bg-white text-orange-600 hover:bg-orange-50 font-bold shadow-md hover:shadow-lg transition-all"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Agregar Beneficio
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filtros y Buscador */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        {/* Categorías Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0 scrollbar-none">
          {CATEGORIAS.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? "bg-orange-500 text-white shadow-sm shadow-orange-500/20"
                  : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Input Buscador */}
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder="Buscar por beneficio o palabra..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-slate-200 text-sm rounded-xl"
          />
        </div>
      </div>

      {/* Grid de Beneficios */}
      {loading ? (
        <div className="py-20 text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Cargando beneficios Corazón Naranja...</p>
        </div>
      ) : filteredBeneficios.length === 0 ? (
        <Card className="border-slate-200 text-center py-16 bg-white shadow-sm">
          <CardContent>
            <div className="w-14 h-14 bg-orange-50 text-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Heart className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No se encontraron beneficios</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              Prueba con otro término de búsqueda o selecciona otra categoría.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBeneficios.map((b) => {
            const IconComp = ICON_MAP[b.icono] || Heart;
            return (
              <Card
                key={b.id}
                onClick={() => setDetailModal(b)}
                className="group border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all duration-300 bg-white cursor-pointer relative overflow-hidden flex flex-col justify-between"
              >
                {b.destacado && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-gradient-to-l from-orange-500 to-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl shadow-sm flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Destacado
                    </div>
                  </div>
                )}

                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="p-3 rounded-2xl bg-orange-50 text-orange-600 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300 shadow-sm">
                      <IconComp className="w-6 h-6" />
                    </div>
                    <Badge variant="outline" className="text-[11px] font-medium border-slate-200 bg-slate-50">
                      {b.categoria}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg font-bold text-slate-800 group-hover:text-orange-600 transition-colors">
                    {b.titulo}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 flex-1 flex flex-col justify-between pb-5">
                  <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">
                    {b.descripcion_corta}
                  </p>

                  {b.requisitos && (
                    <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-2 text-[11px] text-slate-600">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                      <span className="line-clamp-2">
                        <strong className="text-slate-700">Requisito:</strong> {b.requisitos}
                      </span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-orange-600 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                      Ver detalles & solicitar &rarr;
                    </span>

                    {isAdminOrRRHH && (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => handleOpenEdit(b, e)}
                          className="h-7 w-7 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => handleDelete(b, e)}
                          className="h-7 w-7 text-rose-400 hover:text-rose-700 hover:bg-rose-50 rounded-lg"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* MODAL DETALLE DE BENEFICIO */}
      <Dialog open={!!detailModal} onOpenChange={() => setDetailModal(null)}>
        {detailModal && (
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-orange-100 text-orange-600 rounded-2xl">
                  {(() => {
                    const IconComp = ICON_MAP[detailModal.icono] || Heart;
                    return <IconComp className="w-6 h-6" />;
                  })()}
                </div>
                <div>
                  <Badge variant="outline" className="text-xs mb-1 bg-orange-50 border-orange-200 text-orange-700">
                    {detailModal.categoria}
                  </Badge>
                  <DialogTitle className="text-xl font-bold text-slate-800">
                    {detailModal.titulo}
                  </DialogTitle>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm text-slate-700 font-medium leading-relaxed">
                  {detailModal.descripcion_corta}
                </p>
              </div>

              {detailModal.descripcion_larga && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Detalles y Cobertura
                  </h4>
                  <div
                    className="text-xs text-slate-600 space-y-2 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: detailModal.descripcion_larga }}
                  />
                </div>
              )}

              {detailModal.requisitos && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Criterios de Elegibilidad
                  </div>
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    {detailModal.requisitos}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between items-center pt-3 border-t border-slate-100">
              <Button variant="outline" onClick={() => setDetailModal(null)} className="w-full sm:w-auto">
                Cerrar
              </Button>
              {detailModal.enlace_contacto ? (
                <a
                  href={detailModal.enlace_contacto}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto"
                >
                  <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold flex items-center justify-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Solicitar / Acceder al Beneficio
                  </Button>
                </a>
              ) : (
                <div className="text-xs text-slate-500 italic">
                  Para solicitar este beneficio, comunícate con Gestión del Talento Humano / RRHH.
                </div>
              )}
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* MODAL CREAR / EDITAR BENEFICIO */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSave}>
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Heart className="w-5 h-5 text-orange-500 fill-orange-500" />
                {editingItem ? "Editar Beneficio Corazón Naranja" : "Nuevo Beneficio Corazón Naranja"}
              </DialogTitle>
              <DialogDescription>
                Configura los detalles del beneficio que se mostrará en el catálogo institucional.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="titulo">Nombre del Beneficio *</Label>
                <Input
                  id="titulo"
                  placeholder="Ej: Seguro Laboral & Médico, Plan con CPX..."
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="categoria">Categoría</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(val) => setFormData({ ...formData, categoria: val })}
                >
                  <SelectTrigger id="categoria">
                    <SelectValue placeholder="Selecciona categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.filter((c) => c !== "Todas").map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="icono">Ícono Representativo</Label>
                <Select
                  value={formData.icono}
                  onValueChange={(val) => setFormData({ ...formData, icono: val })}
                >
                  <SelectTrigger id="icono">
                    <SelectValue placeholder="Selecciona ícono" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(ICON_MAP).map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="descripcion_corta">Descripción Corta (Resumen para la tarjeta) *</Label>
                <Textarea
                  id="descripcion_corta"
                  rows={2}
                  placeholder="Breve explicación de las ventajas principales del beneficio..."
                  value={formData.descripcion_corta}
                  onChange={(e) => setFormData({ ...formData, descripcion_corta: e.target.value })}
                  required
                />
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="descripcion_larga">Detalles Ampliados (HTML o texto con puntos clave)</Label>
                <Textarea
                  id="descripcion_larga"
                  rows={4}
                  placeholder="<p>Explicación detallada...</p><ul><li>Cobertura 1</li><li>Cobertura 2</li></ul>"
                  value={formData.descripcion_larga}
                  onChange={(e) => setFormData({ ...formData, descripcion_larga: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="requisitos">Requisitos de Elegibilidad</Label>
                <Input
                  id="requisitos"
                  placeholder="Ej: Mínimo 3 meses de antigüedad, contrato indefinido..."
                  value={formData.requisitos}
                  onChange={(e) => setFormData({ ...formData, requisitos: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="enlace_contacto">Enlace de Solicitud / Formulario / WhatsApp</Label>
                <Input
                  id="enlace_contacto"
                  placeholder="https://cpslogistics.com o https://wa.me/..."
                  value={formData.enlace_contacto}
                  onChange={(e) => setFormData({ ...formData, enlace_contacto: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div className="space-y-0.5">
                  <Label className="text-xs font-semibold">Beneficio Activo</Label>
                  <p className="text-[11px] text-slate-500">Visible para todos los colaboradores</p>
                </div>
                <Switch
                  checked={formData.activo}
                  onCheckedChange={(val) => setFormData({ ...formData, activo: val })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div className="space-y-0.5">
                  <Label className="text-xs font-semibold">Marcar como Destacado</Label>
                  <p className="text-[11px] text-slate-500">Muestra una insignia especial en la tarjeta</p>
                </div>
                <Switch
                  checked={formData.destacado}
                  onCheckedChange={(val) => setFormData({ ...formData, destacado: val })}
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white font-bold">
                {saving ? "Guardando..." : editingItem ? "Actualizar Beneficio" : "Guardar Beneficio"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
